import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { Ionicons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { DevChecklistEditorLogger } from '@/utils/logger';

interface DevChecklistDropdownFieldProps {
  tipo: string;
  label: string;
  value?: string;
  onChange: (val: string) => void;
}

const logger = DevChecklistEditorLogger.Data;

const DevChecklistDropdownField: React.FC<DevChecklistDropdownFieldProps> = ({
  tipo,
  label,
  value,
  onChange,
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { showActionSheetWithOptions } = useActionSheet();

  useEffect(() => {
    let mounted = true;
    const loadOptions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_DROPDOWN.NAME)
          .select('valor')
          .eq('tipo', tipo)
          .order('valor', { ascending: true });

        if (error) {
          throw error;
        }

        if (!mounted) return;
        setOptions((data ?? []).map(item => item.valor));
      } catch (err) {
        logger.warn('[DevChecklistDropdownField] Falha ao carregar opções', err);
        if (mounted) {
          setOptions([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadOptions();
    return () => {
      mounted = false;
    };
  }, [tipo]);

  const openPicker = () => {
    if (loading) return;
    const sheetOptions = ['Fechar', 'Nenhum', ...options];
    showActionSheetWithOptions(
      {
        options: sheetOptions,
        cancelButtonIndex: 0,
        title: 'Selecionar opção',
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      (buttonIndex?: number) => {
        if (buttonIndex === undefined || buttonIndex === 0) return;
        if (buttonIndex === 1) {
          onChange('');
        } else {
          const selectedValue = options[buttonIndex - 2];
          if (selectedValue !== undefined) {
            onChange(selectedValue);
          }
        }
      }
    );
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity
        style={styles.selectButton}
        onPress={openPicker}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator size="small" color={theme.colors.primary.default} />
        ) : (
          <>
            <Text style={styles.valueText}>
              {value ? value : 'Selecionar...'}
            </Text>
            <Ionicons
              name="chevron-down"
              size={18}
              color={theme.colors.text.secondary}
              style={{ marginLeft: 8 }}
            />
          </>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 12,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: 4,
  },
  selectButton: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.sm + 4,
    paddingVertical: theme.spacing.xs + 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  valueText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
});

export default DevChecklistDropdownField;

